import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Info, XCircle, Loader2, Power, PowerOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminMaintenancePage = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [bannerSettings, setBannerSettings] = useState({
    enabled: false,
    message: '',
    type: 'info'
  });

  useEffect(() => {
    checkAdmin();
    fetchBannerSettings();
  }, []);

  const checkAdmin = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setIsAdmin(user.is_admin === true);
    }
    setIsLoading(false);
  };

  const fetchBannerSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/maintenance/banner`);
      setBannerSettings({
        enabled: response.data.enabled || false,
        message: response.data.message || '',
        type: response.data.type || 'info'
      });
    } catch (err) {
      console.error('Failed to fetch banner settings:', err);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.put(`${BACKEND_URL}/api/maintenance/banner`, bannerSettings);
      if (response.data.success) {
        setSuccess('Banner settings saved successfully!');
        setBannerSettings({
          enabled: response.data.enabled,
          message: response.data.message,
          type: response.data.type
        });
      }
    } catch (err) {
      setError('Failed to save banner settings');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !bannerSettings.enabled;
    setBannerSettings(prev => ({ ...prev, enabled: newEnabled }));
    
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.put(`${BACKEND_URL}/api/maintenance/banner`, {
        ...bannerSettings,
        enabled: newEnabled
      });
      if (response.data.success) {
        setSuccess(newEnabled ? 'Maintenance banner is now LIVE!' : 'Maintenance banner has been disabled.');
      }
    } catch (err) {
      setError('Failed to toggle banner');
      setBannerSettings(prev => ({ ...prev, enabled: !newEnabled }));
    } finally {
      setIsSaving(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const presetMessages = [
    "We're performing scheduled maintenance. The app will be back shortly!",
    "System update in progress. Some features may be temporarily unavailable.",
    "We're deploying new features! Please refresh your browser in a few minutes.",
    "Brief maintenance window - we'll be back online shortly. Thank you for your patience!"
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="p-8 dark:bg-slate-800">
          <p className="text-red-600 dark:text-red-400">Admin access required</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo variant="plain" size="lg" data-testid="brand-logo-standalone" />
            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">KaiCheckIt</span>
            <Badge variant="outline" className="ml-2">Admin</Badge>
          </div>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">Maintenance Banner</h1>
            <p className="text-slate-600 dark:text-slate-400">Notify users before deployments or during maintenance</p>
          </div>

          {/* Alerts */}
          {error && (
            <Alert className="mb-4 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
              <AlertDescription className="text-red-800 dark:text-red-300">{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800">
              <AlertDescription className="text-green-800 dark:text-green-300">{success}</AlertDescription>
            </Alert>
          )}

          {/* Status Card */}
          <Card className={`mb-6 dark:bg-slate-800 ${bannerSettings.enabled ? 'border-2 border-green-500' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="dark:text-slate-100">Banner Status</CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    {bannerSettings.enabled ? 'Banner is currently visible to all users' : 'Banner is currently hidden'}
                  </CardDescription>
                </div>
                <Button
                  onClick={handleToggle}
                  disabled={isSaving}
                  className={bannerSettings.enabled 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                  }
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : bannerSettings.enabled ? (
                    <PowerOff className="w-4 h-4 mr-2" />
                  ) : (
                    <Power className="w-4 h-4 mr-2" />
                  )}
                  {bannerSettings.enabled ? 'Turn Off' : 'Turn On'}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Settings Card */}
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="dark:text-slate-100">Banner Settings</CardTitle>
              <CardDescription className="dark:text-slate-400">
                Configure the message and style of the maintenance banner
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Banner Type */}
              <div>
                <Label htmlFor="type" className="dark:text-slate-200">Banner Type</Label>
                <Select
                  value={bannerSettings.type}
                  onValueChange={(value) => setBannerSettings(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-500" />
                        Info (Blue)
                      </div>
                    </SelectItem>
                    <SelectItem value="warning">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Warning (Amber)
                      </div>
                    </SelectItem>
                    <SelectItem value="critical">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Critical (Red)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="dark:text-slate-200">Message</Label>
                <Input
                  id="message"
                  value={bannerSettings.message}
                  onChange={(e) => setBannerSettings(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter your maintenance message..."
                  className="mt-1"
                />
              </div>

              {/* Preset Messages */}
              <div>
                <Label className="dark:text-slate-200">Quick Presets</Label>
                <div className="grid gap-2 mt-2">
                  {presetMessages.map((msg, idx) => (
                    <button
                      key={idx}
                      onClick={() => setBannerSettings(prev => ({ ...prev, message: msg }))}
                      className="text-left text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                    >
                      {msg}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {bannerSettings.message && (
                <div>
                  <Label className="dark:text-slate-200">Preview</Label>
                  <div className={`mt-2 p-3 rounded-lg flex items-center gap-3 ${
                    bannerSettings.type === 'critical' ? 'bg-red-600 text-white' :
                    bannerSettings.type === 'warning' ? 'bg-amber-500 text-white' :
                    'bg-blue-600 text-white'
                  }`}>
                    {getTypeIcon(bannerSettings.type)}
                    <span className="text-sm font-medium">{bannerSettings.message}</span>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Save Settings
              </Button>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="mt-6 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Deployment Workflow</h3>
              <ol className="text-sm text-amber-700 dark:text-amber-400 space-y-1 list-decimal list-inside">
                <li>Turn on the banner with your message</li>
                <li>Wait a few minutes for users to see the notice</li>
                <li>Deploy your changes in Emergent</li>
                <li>After deployment completes, turn off the banner</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminMaintenancePage;
